import React, { useState, useEffect, useRef } from 'react'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react"
import { addAdminMessage, markMessagesAsRead, subscribeToChat } from '../../firebase/supportChats'
import { getIncomeIdsForSettlement, settleIncomes } from '../../firebase/incomes'
import DialogConfirmation from './DialogConfirmation'
const { useAuth } = require('../../context/authContext')

const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString()
}

export default function DialogSettlement({
    open,
    onClose,
}) {
    const [loading, setLoading] = useState(false)
    const [incomeIds, setIncomeIds] = useState([])
    const [description, setDescription] = useState('')
    const [totalAmount, setTotalAmount] = useState(0)
    const [error, setError] = useState(null)
    const { user } = useAuth()

    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [settlementInProgress, setSettlementInProgress] = useState(false)
    const [settlementFinished, setSettlementFinished] = useState(false)

    const handleSettlement = async () => {
        console.log('Settling incomes:', incomeIds, 'Description:', description)
        setSettlementInProgress(true)
        try {
            const result= await settleIncomes(incomeIds, user.uid, description)
            console.log('Settlement result:', result)
            setSettlementInProgress(false)
            setSettlementFinished(true)
            setShowConfirmModal(true)
        } catch (error) {
            console.error('Error settling incomes:', error)
            setSettlementInProgress(false)
            setError(error.message || 'Failed to settle incomes')
        }
    }
    
    const reset = () => {
        setDescription('')
        setIncomeIds([])
        setTotalAmount(0)
        setError(null)
        setSettlementInProgress(false)
        setSettlementFinished(false)
        setShowConfirmModal(false)
    }

    useEffect(() => {
        if (open) {
            reset();
            // Fetch pending incomes and calculate total amount
            const fetchPendingIncomes = async () => {
                setLoading(true)
                setError(null)
                try {
                    const data = await getIncomeIdsForSettlement(user.uid)
                    setIncomeIds(data.incomeIds)
                    setTotalAmount(data.totalAmount)
                } catch (error) {
                    console.error('Error fetching settleable incomes:', error)
                    setError(error.message)
                } finally {
                    setLoading(false)
                    setError(null)
                }
            }
            fetchPendingIncomes()
        } else {
            setIncomeIds([])
            setTotalAmount(0)
        }
    }, [open, user.uid]);
    return (
        <>
            <DialogConfirmation
                message="Settlement completed successfully!"
                open={showConfirmModal}
                onClose={() => {
                    setShowConfirmModal(false)
                    onClose(true) // Pass true to indicate successful settlement
                }}
                buttonText="Done"
                title="Settlement Completed"
                iconSrc="/assets/icons/icon-check-circle.svg"
                type="confirm"
            />
            <Dialog open={open} onClose={onClose} className="relative z-[10000]">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-75 transition-opacity"
                />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
                        <DialogTitle className="text-xl font-bold mb-4">Settle Income</DialogTitle>
                        
                        {!settlementInProgress && !settlementFinished && (
                            <div className="space-y-4">
                                {loading && <p className="text-gray-600">Loading...</p>}
                                {error && <p className="text-red-600">{error}</p>}
                                {!loading && !error && (incomeIds.length === 0 || totalAmount === 0) && (
                                    <p className="text-gray-600">No pending incomes to settle.</p>
                                )}
                                {!loading && !error && (incomeIds.length > 0 && totalAmount > 0) && (
                                    <>
                                        <p className="text-gray-600">The {incomeIds.length} incomes are pending settlement:</p>
                                        <p className="text-gray-600">Total Amount: ${totalAmount.toFixed(2)}</p>
                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                                Description
                                            </label>
                                            <textarea
                                                id="description"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                                                rows={3}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="flex gap-3 justify-end">
                                    <button
                                        onClick={onClose}
                                        className="px-4 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button className={`px-4 py-2 rounded-md ${loading || error || incomeIds.length === 0 || description.trim() === '' ? 'bg-gray-300 text-gray-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                        disabled={loading || error || incomeIds.length === 0 || description.trim() === ''}
                                        onClick={() => { 
                                            handleSettlement()
                                        }}
                                    >
                                        Settle
                                    </button>
                                </div>
                            </div>
                        )}
                        {settlementInProgress && (
                            <div className="text-center">
                                <p className="text-gray-600">Settling incomes...</p>
                            </div>
                        )}
                        {settlementFinished && (
                            <div className="text-center">
                                <p className="text-green-600">Incomes settled successfully!</p>
                                <button
                                    onClick={onClose}
                                    className="mt-4 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </DialogPanel>
                </div>
            </Dialog>
        </>
    )
} 